import React, {
    useRef, useCallback, useState, useMemo, useEffect,
} from 'react';
import SignatureCanvas from 'react-signature-canvas';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button, Paper, Typography, Box, Tabs, Tab, TextField,
} from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import ImageIcon from '@material-ui/icons/Image';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

const useStyles = makeStyles(() => ({
    container: {
        padding: '1rem',
        backgroundColor: '#F7F9FF',
        minWidth: '400px',
    },
    title: {
        fontFamily: 'Montserrat',
        fontWeight: 'bold',
        marginBottom: '0.5rem',
        textAlign: 'center',
        color: '#15325C',
    },
    tabs: {
        marginBottom: '1rem',
        '& .MuiTabs-indicator': {
            backgroundColor: '#C00333',
        },
    },
    tab: {
        fontFamily: 'Montserrat',
        fontWeight: '600',
        fontSize: '0.75rem',
        minWidth: '100px',
        '&.Mui-selected': {
            color: '#C00333',
        },
    },
    canvasContainer: {
        border: '2px dashed #C00333',
        borderRadius: '0.625rem',
        backgroundColor: '#FFFFFF',
        marginBottom: '1rem',
        overflow: 'hidden',
    },
    canvas: {
        width: '100%',
        height: '200px',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '1rem',
    },
    clearButton: {
        flex: 1,
        backgroundColor: '#F5F8FF',
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        color: '#C00333',
        paddingTop: '0.625rem',
        paddingBottom: '0.625rem',
        borderRadius: '0.625rem',
        boxShadow: '0rem 0.125rem 0.25rem #ED2D5D33',
        textTransform: 'capitalize',
        fontWeight: '600',
        '&:hover': {
            backgroundColor: '#E8ECFF',
        },
    },
    confirmButton: {
        flex: 1,
        background: 'linear-gradient(90deg, #E02A58, #C00333)',
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        color: '#FFFFFF',
        paddingTop: '0.625rem',
        paddingBottom: '0.625rem',
        borderRadius: '0.625rem',
        boxShadow: '0.125rem 0.25rem 0.625rem #ED2D5D33',
        textTransform: 'capitalize',
        fontWeight: '600',
        '&:hover': {
            background: 'linear-gradient(90deg, #C00333, #A00228)',
        },
        '&:disabled': {
            background: '#CCCCCC',
            color: '#666666',
        },
    },
    hint: {
        fontFamily: 'Montserrat',
        fontSize: '0.75rem',
        color: '#666666',
        textAlign: 'center',
        marginBottom: '0.5rem',
    },
    // Estilos para firma escrita
    typedInput: {
        marginBottom: '1rem',
        '& .MuiOutlinedInput-root': {
            fontFamily: 'Montserrat',
            fontSize: '1.25rem',
            textAlign: 'center',
            '& input': {
                textAlign: 'center',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#C00333',
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#C00333',
        },
    },
    typedPreview: {
        border: '2px dashed #C00333',
        borderRadius: '0.625rem',
        backgroundColor: '#FFFFFF',
        padding: '1.5rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '120px',
    },
    typedPreviewImage: {
        maxWidth: '100%',
        maxHeight: '100px',
        objectFit: 'contain',
    },
    // Estilos para imagen
    uploadArea: {
        border: '2px dashed #C00333',
        borderRadius: '0.625rem',
        backgroundColor: '#FFFFFF',
        padding: '2rem',
        marginBottom: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        minHeight: '150px',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: 'rgba(192, 3, 51, 0.05)',
        },
    },
    uploadIcon: {
        fontSize: '3rem',
        color: '#C00333',
        marginBottom: '0.5rem',
    },
    uploadText: {
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        color: '#666666',
    },
    imagePreview: {
        maxWidth: '100%',
        maxHeight: '150px',
        objectFit: 'contain',
        borderRadius: '0.5rem',
    },
    hiddenInput: {
        display: 'none',
    },
}));

// Firma escrita: el lienzo mantiene la proporcion 3:1 con la que se estampa
// la firma en el PDF (150x50 puntos), para que el nombre no se deforme
const TYPED_CANVAS_WIDTH = 600;
const TYPED_CANVAS_HEIGHT = 200;
const TYPED_PADDING = 30;
// Un nombre completo cabe en 40 caracteres con holgura
const TYPED_MAX_CHARS = 40;
const TYPED_MAX_FONT = 90;
const TYPED_MIN_FONT = 22;
// Great Vibes va empaquetada (@fontsource/great-vibes, importada en index.scss),
// asi el trazo cursivo es el mismo en Windows, Android, iOS y Linux. Los
// respaldos del sistema solo actuan si la fuente no llegara a cargar.
const TYPED_SCRIPT_FONT = "'Great Vibes'";
// Tope de espera de la fuente: si no resuelve se dibuja con el respaldo del
// sistema, en vez de dejar la pestana inutilizable
const TYPED_FONT_TIMEOUT = 3000;
const TYPED_FONT_STACK = `${TYPED_SCRIPT_FONT}, 'Brush Script MT', 'Segoe Script', cursive`;

/**
 * @name fitFontSize
 * @desc Busca el mayor tamano de fuente con el que el texto cabe en el ancho dado.
 * Sin esto un nombre largo se dibujaria recortado, que era el motivo del limite
 * de 5 caracteres.
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} maxWidth
 * @returns {number} tamano de fuente en px
 */
const fitFontSize = (ctx, text, maxWidth) => {
    let size = TYPED_MAX_FONT;

    while (size > TYPED_MIN_FONT) {
        ctx.font = `${size}px ${TYPED_FONT_STACK}`;
        if (ctx.measureText(text).width <= maxWidth) {
            return size;
        }
        size -= 2;
    }

    return TYPED_MIN_FONT;
};

// eslint-disable-next-line no-unused-vars
function SignaturePad({ onSignatureConfirm, onCancel }) {
    const classes = useStyles();
    const signatureRef = useRef(null);
    const fileInputRef = useRef(null);

    // Estados
    const [tabValue, setTabValue] = useState(0);
    const [isEmpty, setIsEmpty] = useState(true);
    const [typedName, setTypedName] = useState('');
    const [uploadedImage, setUploadedImage] = useState(null);
    const [fontReady, setFontReady] = useState(false);

    // El canvas no espera a las webfonts: si se dibuja antes de que la cursiva
    // este disponible, se usaria el respaldo del sistema y las medidas del
    // ajuste de tamano saldrian mal. Se espera a que cargue.
    useEffect(() => {
        let cancelado = false;

        const cargarFuente = async () => {
            try {
                if (document.fonts && document.fonts.load) {
                    await Promise.race([
                        document.fonts.load(`${TYPED_MAX_FONT}px ${TYPED_SCRIPT_FONT}`),
                        new Promise((terminar) => {
                            setTimeout(terminar, TYPED_FONT_TIMEOUT);
                        }),
                    ]);
                }
            } catch (e) {
                // Si no carga se continua con el respaldo del sistema
            }
            if (!cancelado) {
                setFontReady(true);
            }
        };

        cargarFuente();

        return () => {
            cancelado = true;
        };
    }, []);

    // Handlers para firma dibujada
    const handleClear = useCallback(() => {
        if (signatureRef.current) {
            signatureRef.current.clear();
            setIsEmpty(true);
        }
    }, []);

    const handleEnd = useCallback(() => {
        if (signatureRef.current) {
            setIsEmpty(signatureRef.current.isEmpty());
        }
    }, []);

    const handleConfirmDrawn = useCallback(() => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            const dataUrl = signatureRef.current.toDataURL('image/png');
            onSignatureConfirm(dataUrl);
        }
    }, [onSignatureConfirm]);

    // Handlers para firma escrita
    const handleTypedNameChange = useCallback((event) => {
        setTypedName(event.target.value.slice(0, TYPED_MAX_CHARS));
    }, []);

    /**
     * @name typedImage
     * @desc Imagen de la firma escrita. Se usa tanto para la vista previa como
     * para lo que se confirma, asi lo que ve el usuario es exactamente lo que
     * se estampa en el documento.
     */
    const typedImage = useMemo(() => {
        const texto = typedName.trim();
        if (!texto || !fontReady) return null;

        const canvas = document.createElement('canvas');
        canvas.width = TYPED_CANVAS_WIDTH;
        canvas.height = TYPED_CANVAS_HEIGHT;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        // Sin fondo (transparente) para no tapar el texto del documento donde se
        // estampa; la vista previa se ve blanca por el contenedor

        // El texto se reduce hasta caber; el maxWidth de fillText es la garantia final
        const anchoUtil = canvas.width - (TYPED_PADDING * 2);
        const fontSize = fitFontSize(ctx, texto, anchoUtil);

        ctx.font = `${fontSize}px ${TYPED_FONT_STACK}`;
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(texto, canvas.width / 2, canvas.height / 2, anchoUtil);

        return canvas.toDataURL('image/png');
    }, [typedName, fontReady]);

    const handleConfirmTyped = useCallback(() => {
        if (typedImage) {
            onSignatureConfirm(typedImage);
        }
    }, [typedImage, onSignatureConfirm]);

    // Handlers para imagen
    const handleImageUpload = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setUploadedImage(e.target.result);
        };
        reader.readAsDataURL(file);
    }, []);

    const handleUploadClick = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const handleClearImage = useCallback(() => {
        setUploadedImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleConfirmImage = useCallback(() => {
        if (uploadedImage) {
            onSignatureConfirm(uploadedImage);
        }
    }, [uploadedImage, onSignatureConfirm]);

    // Cambio de tab
    const handleTabChange = useCallback((event, newValue) => {
        setTabValue(newValue);
    }, []);

    // Renderizar contenido segun tab
    const renderTabContent = () => {
        switch (tabValue) {
            case 0: // Dibujar
                return (
                  <>
                    <Typography className={classes.hint}>
                      Use el mouse o el dedo para dibujar su firma
                    </Typography>
                    <Box className={classes.canvasContainer}>
                      <SignatureCanvas
                        ref={signatureRef}
                        canvasProps={{
                                className: classes.canvas,
                            }}
                        penColor="#000000"
                        // Fondo transparente: con fondo blanco la firma tapaba el
                        // texto del documento que quedaba debajo (el contenedor
                        // ya se ve blanco por CSS)
                        backgroundColor="rgba(0,0,0,0)"
                        onEnd={handleEnd}
                      />
                    </Box>
                    <Box className={classes.buttonContainer}>
                      <Button
                        variant="contained"
                        className={classes.clearButton}
                        onClick={handleClear}
                      >
                        Limpiar
                      </Button>
                      <Button
                        variant="contained"
                        className={classes.confirmButton}
                        onClick={handleConfirmDrawn}
                        disabled={isEmpty}
                      >
                        Confirmar
                      </Button>
                    </Box>
                  </>
                );

            case 1: // Firma escrita
                return (
                  <>
                    <Typography className={classes.hint}>
                      Escriba su nombre tal como quiere que aparezca en la firma
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Nombre y apellidos"
                      value={typedName}
                      onChange={handleTypedNameChange}
                      className={classes.typedInput}
                      inputProps={{ maxLength: TYPED_MAX_CHARS }}
                    />
                    <Box className={classes.typedPreview}>
                      {typedImage ? (
                        <img
                          src={typedImage}
                          alt="Vista previa de la firma"
                          className={classes.typedPreviewImage}
                        />
                            ) : (
                              <Typography style={{ color: '#999', fontFamily: 'Montserrat' }}>
                                Vista previa de la firma
                              </Typography>
                            )}
                    </Box>
                    <Box className={classes.buttonContainer}>
                      <Button
                        variant="contained"
                        className={classes.clearButton}
                        onClick={() => setTypedName('')}
                      >
                        Limpiar
                      </Button>
                      <Button
                        variant="contained"
                        className={classes.confirmButton}
                        onClick={handleConfirmTyped}
                        disabled={!typedImage}
                      >
                        Confirmar
                      </Button>
                    </Box>
                  </>
                );

            case 2: // Imagen
                return (
                  <>
                    <Typography className={classes.hint}>
                      Cargue una imagen de su firma
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      ref={fileInputRef}
                      className={classes.hiddenInput}
                    />
                    {!uploadedImage ? (
                      <Box
                        className={classes.uploadArea}
                        onClick={handleUploadClick}
                      >
                        <CloudUploadIcon className={classes.uploadIcon} />
                        <Typography className={classes.uploadText}>
                          Haga clic para seleccionar una imagen
                        </Typography>
                        <Typography
                          className={classes.uploadText}
                          style={{ fontSize: '0.75rem' }}
                        >
                          PNG, JPG, JPEG
                        </Typography>
                      </Box>
                        ) : (
                          <Box
                            className={classes.uploadArea}
                            style={{ cursor: 'default' }}
                          >
                            <img
                              src={uploadedImage}
                              alt="Firma cargada"
                              className={classes.imagePreview}
                            />
                          </Box>
                        )}
                    <Box className={classes.buttonContainer}>
                      <Button
                        variant="contained"
                        className={classes.clearButton}
                        onClick={uploadedImage ? handleClearImage : handleUploadClick}
                      >
                        {uploadedImage ? 'Cambiar' : 'Seleccionar'}
                      </Button>
                      <Button
                        variant="contained"
                        className={classes.confirmButton}
                        onClick={handleConfirmImage}
                        disabled={!uploadedImage}
                      >
                        Confirmar
                      </Button>
                    </Box>
                  </>
                );

            default:
                return null;
        }
    };

    return (
      <Paper className={classes.container} elevation={3}>
        <Typography variant="h6" className={classes.title}>
          Crear firma
        </Typography>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          className={classes.tabs}
          variant="fullWidth"
          centered
        >
          <Tab
            icon={<CreateIcon />}
            label="Dibujar"
            className={classes.tab}
          />
          <Tab
            icon={<TextFieldsIcon />}
            label="Escribir"
            className={classes.tab}
          />
          <Tab
            icon={<ImageIcon />}
            label="Imagen"
            className={classes.tab}
          />
        </Tabs>

        {renderTabContent()}
      </Paper>
    );
}

SignaturePad.defaultProps = {
    onCancel: null,
};

SignaturePad.propTypes = {
    onSignatureConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
};

export default SignaturePad;
